import React, { useState } from "react";
import axios from "axios";
import { Bell, Check } from "lucide-react";
import { Button } from "./ui/button";

/**
 * SaveMySlotButton — one-tap "watch this exact slot" hold.
 *
 * When the user's preferred slot is no longer available (or they
 * just want a specific date/time locked in), one tap stores a
 * server-side `slot_hold`. The moment a matching Square booking is
 * cancelled, the backend auto-books THIS client via the shared
 * booking impl (idempotency-key safe) and emails them the
 * confirmation.
 *
 * Props are the minimum context the parent booking flow already has.
 * The component:
 *   • Hits POST /api/waitlist/slot-hold
 *   • Shows an inline success state on success
 *   • Shows a compact error message on failure
 */
export default function SaveMySlotButton({
  API,
  locationId,
  date,
  startTime,
  provider,
  serviceName,
  serviceVariationId,
  serviceDurationMinutes,
  services,
  serviceSegments,
  customer,
  isFr = false,
  disabled = false,
  className = "",
}) {
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const canSubmit = Boolean(
    !disabled
    && locationId
    && date
    && startTime
    && customer?.email
    && customer?.first_name
    && customer?.last_name
  );

  const onClick = async () => {
    if (!canSubmit || status === "loading") return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/waitlist/slot-hold`, {
        location_id: String(locationId),
        date,
        start_time: startTime,
        provider: provider || null,
        service_variation_id: serviceVariationId || null,
        service_name: serviceName || null,
        service_duration_minutes: serviceDurationMinutes || 30,
        services: services || null,
        service_segments: serviceSegments || null,
        customer_first_name: customer.first_name,
        customer_last_name: customer.last_name,
        customer_email: customer.email,
        customer_phone: customer.phone || "",
        notes: "",
      }, { timeout: 10000 });
      if (res?.data?.success) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg(res?.data?.message || (isFr ? "Impossible d'enregistrer le créneau." : "Couldn't save the slot."));
      }
    } catch (e) {
      setStatus("error");
      setErrorMsg(
        e?.response?.data?.detail
        || e?.message
        || (isFr ? "Erreur réseau." : "Network error.")
      );
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="save-my-slot-success"
        className={`flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300 ${className}`}
      >
        <Check className="h-4 w-4 flex-none" />
        <span>
          {isFr
            ? "C'est fait! Nous vous réserverons automatiquement dès qu'une place se libère."
            : "You're on! We'll auto-book you the moment this slot opens up."}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Button
        type="button"
        variant="outline"
        onClick={onClick}
        disabled={!canSubmit || status === "loading"}
        data-testid="save-my-slot-btn"
        className="text-sm border-amber-500/60 text-amber-300 hover:bg-amber-500/10"
      >
        <Bell className="w-4 h-4 mr-2" />
        {status === "loading"
          ? (isFr ? "Enregistrement…" : "Saving…")
          : (isFr ? "Enregistrer ce créneau · Réservation auto" : "Save this slot · Auto-book me")}
      </Button>
      {status === "error" && errorMsg && (
        <p data-testid="save-my-slot-error" className="text-xs text-red-400">{errorMsg}</p>
      )}
      {!canSubmit && status !== "error" && (
        <p className="text-xs text-zinc-500">
          {isFr
            ? "Remplissez d'abord vos coordonnées et sélectionnez un créneau."
            : "Fill in your info and pick a slot first."}
        </p>
      )}
    </div>
  );
}
